import { Component, input, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { selectCanRelease, selectCheckInfo, selectClientAuthNeeded, selectEauthModel, selectEditFLowFromTableComplete, selectHasSubmitAndCreateError, selectInternalTransferInfo, selectInternalTransfersInfo, selectIs401kToAccount, selectIsBundledOneTimeTotalDist, selectIsOneTimeTotalDist, selectIsPendingAuthorization, selectIsSubmitAndCreate, selectPendForReleaseCustomRule, selectPendReasons, selectStagedTransactionType, selectTransactionForm, selectWireInfo } from '../../../create-request/reducers';
import { CodeAndDescription } from '../../../create-request/models/shared/CodeAndDescription';
import { confirmationStatusMessage, digitalRegTypes, pendReasonsStrings, sourceOfLastModification, transactionTypes, yesNo } from '../../utilities/Constants';
import { takeUntil } from 'rxjs/operators';
import { EAuthInfo } from '../../models/EAuthInfo';
import { ChecksExtraPrompts, ItExtraPrompts, WireExtraPrompts } from '../../../create-request/models/form/TransactionForm';
import { selectCaseId, selectEAuthInfo, selectInitiatedBy, selectRequestedDate, selectRequestedTime, selectTransactionStatus } from '../../reducers';
import { NamePipe } from '../../pipes/NamePipe';
import { selectEAuthCustomers, selectRegType, selectUsername } from '../../../account-info-header/reducers';
import { Customers } from '../../../account-info-header/models/AccountInfoApiResponse';
import { EAuthStatusMessageFields } from '../../../create-request/models/internal-transfer/InternalTransferInquiryResponse';
import { SubmitAndCreateStatus } from '../../../create-request/models/shared/SubmitAndCreateStatus';
import { FeatureFlagService } from '../../services/feature-flag-service';
import { selectHasAccessInitiateMoneyMovement, selectHasAccessReleaseMoneyMovement } from '../../../authorize/reducers';
import { selectHasPositionLotsPhase2 } from '../../../authorize/reducers';
import { InternalTransferListAcknowledgeResponseById, InternalTransfersInfo } from '../../../create-request/models/internal-transfer/InternalTransfersInfo';
import { selectInitReleaseInitiatedBy } from '../../../initiate-and-release/reducers';
import { InternalTransferVerifyResponse } from '../../../create-request/models/internal-transfer/InternalTransferVerifyResponse';
import { TitleCasePipe } from '@angular/common';
import { AuthResponse } from '../../../create-request/models/shared/AuthResponse';

@Component({
  standalone: false,
  selector: 'mm-confirmation-details-section',
  templateUrl: './confirmation-details-section.component.html',
  styleUrls: ['./confirmation-details-section.component.scss']
})
export class ConfirmationDetailsSectionComponent implements OnInit {
  @Input() toAccountInContext: string = '';
  @Input() useCondensedLayout: boolean = false;
  @Input() draftInContext: boolean = false;
  @Input() journalAuthResponse: AuthResponse;
  caseId$: Observable<string>;
  transactionStatus$: Observable<CodeAndDescription>;
  requestedTime$: Observable<string>;
  requestedDate$: Observable<string>;
  showWarningText: boolean;
  eAuthInfo: EAuthInfo;
  extraPrompts: ItExtraPrompts;
  destroy$: Subject<boolean> = new Subject<boolean>();
  readonly pendReasonStrings = pendReasonsStrings;
  namePipe = new NamePipe();
  transactionType: string;
  eAuthCustomers: Customers[];
  isPendingAuthorization: boolean;
  status: string;
  pendReasons: string[];
  positionLotsPhase2EnabledFsg: boolean = false;
  itsInfo: InternalTransfersInfo;
  itAckListResponse: InternalTransferListAcknowledgeResponseById = {};
  statusEauthmessage: any;
  statusMessageList: string[];
  hasMmInitiateAccess: boolean;
  hasMmReleaseAccess: boolean;
  username: string;
  canRelease: boolean;
  initiatedBy: string;
  isFromEdit: boolean;
  itjournalVerifyResponse: InternalTransferVerifyResponse[] = [];
  eauthInfo: EAuthInfo;
  isOneTimeTotalDist: boolean = false;
  submittedEasternTime: string = '';
  regType: string = '';
  isChecksBundledOneTimeTotalDist: boolean;
  is401kToAccount: boolean;
  isNotInitReleaseFlowCustomRules: boolean = false;
  checkExtraPrompts: ChecksExtraPrompts;
  wiresExtraPrompts: WireExtraPrompts;
  eAuthModel: any;

  constructor(private store: Store, private ffs: FeatureFlagService, private titleCasePipe: TitleCasePipe) { }

  ngOnInit(): void {
    this.caseId$ = this.store.select(selectCaseId);
    this.transactionStatus$ = this.store.select(selectTransactionStatus);
    this.requestedTime$ = this.store.select(selectRequestedTime);
    this.requestedDate$ = this.store.select(selectRequestedDate);

    this.store.select(selectStagedTransactionType).pipe(takeUntil(this.destroy$)).subscribe(type => this.transactionType = type);

    this.store.select(selectHasPositionLotsPhase2).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.positionLotsPhase2EnabledFsg = val;
    });

    this.store.select(selectTransactionForm).pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.extraPrompts = data.extraPrompts as ItExtraPrompts;
      this.checkExtraPrompts = data.extraPrompts as ChecksExtraPrompts;
      this.wiresExtraPrompts = data.extraPrompts as WireExtraPrompts;
      this.isOneTimeTotalDist = this.extraPrompts?.frequency === 'onetimeTotalDist';
    });

    // digital to pdf flow - going-digital flag removed; feature always enabled
    this.store.select(selectRegType).pipe(takeUntil(this.destroy$)).subscribe((regType) => {
      this.regType = regType
    });

    this.store.select(selectIs401kToAccount).pipe(takeUntil(this.destroy$)).subscribe(is401kToAccount => {
      this.is401kToAccount = is401kToAccount;
    });

    this.store.select(selectEAuthInfo).pipe(takeUntil(this.destroy$)).subscribe(eAuthInfo => {
      this.eAuthInfo = eAuthInfo;
    });

    this.store.select(selectClientAuthNeeded).pipe(takeUntil(this.destroy$)).subscribe(clientAuthNeeded => {
      this.showWarningText = clientAuthNeeded && this.extraPrompts.useStandingAuth === yesNo.no && this.eAuthInfo.isOptedOut;
    });
    
    this.store.select(selectEauthModel).pipe(takeUntil(this.destroy$)).subscribe(eAuthModel => {
      this.eAuthModel = eAuthModel;
    });

    combineLatest([
      this.store.select(selectIsOneTimeTotalDist),
      this.store.select(selectIsBundledOneTimeTotalDist)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([isOneTimeTotalDist, isBundledOneTimeTotalDist]) => {
      this.isOneTimeTotalDist = isOneTimeTotalDist;
      if (this.isOneTimeTotalDist || this.is401kToAccount) {
        this.submittedEasternTime = this.getCurrentEasternTimeString();
      }
      this.isChecksBundledOneTimeTotalDist = isBundledOneTimeTotalDist;
    });

    this.store.select(selectIsPendingAuthorization).pipe(takeUntil(this.destroy$)).subscribe(isPendingAuthorization => this.isPendingAuthorization = isPendingAuthorization);

    this.store.select(selectPendReasons).pipe(takeUntil(this.destroy$)).subscribe(pendReasons => {
      this.pendReasons = pendReasons;
    });

    this.store.select(selectCanRelease).pipe(takeUntil(this.destroy$)).subscribe(canRelease => {this.canRelease = canRelease});
    this.store.select(selectHasAccessInitiateMoneyMovement).pipe(takeUntil(this.destroy$)).subscribe(hasMmInitiateAccess => this.hasMmInitiateAccess = hasMmInitiateAccess);
    this.store.select(selectHasAccessReleaseMoneyMovement).pipe(takeUntil(this.destroy$)).subscribe(hasMmReleaseAccess => this.hasMmReleaseAccess = hasMmReleaseAccess);
    this.store.select(selectUsername).pipe(takeUntil(this.destroy$)).subscribe(username => this.username = username)
    this.store.select(selectEditFLowFromTableComplete).pipe(takeUntil(this.destroy$)).subscribe(EditFLowFromTableComplete => this.isFromEdit = EditFLowFromTableComplete);
    this.store.select(selectPendForReleaseCustomRule).pipe(takeUntil(this.destroy$)).subscribe(noRuleReturned => this.isNotInitReleaseFlowCustomRules = noRuleReturned);

    combineLatest([
      this.store.select(selectInitiatedBy),
      this.store.select(selectInitReleaseInitiatedBy)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([initiatedBy, inRInitiatedBy]) => {
      this.initiatedBy = inRInitiatedBy ? inRInitiatedBy : initiatedBy ?? ''
    });

    combineLatest([
      this.store.select(selectTransactionStatus),
      this.store.select(selectEAuthInfo),
      this.store.select(selectEAuthCustomers),
      this.store.select(selectIsSubmitAndCreate),
      this.store.select(selectHasSubmitAndCreateError),
      this.store.select(selectInternalTransfersInfo)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([status, eAuthInfo, eAuthCustomers, isSubmitAndCreate, submitAndCreateStatus, itsInfo]) => {
      this.itsInfo = itsInfo;
      this.eauthInfo = eAuthInfo;
      this.eAuthCustomers = eAuthCustomers;
      this.itAckListResponse = itsInfo.multipleAcknowledgeListById;
      this.itjournalVerifyResponse = itsInfo.multipleToJournalsList;
      let eAuthApprovers = eAuthCustomers;

      if (this.transactionType === transactionTypes.internalTransfer) {
        this.statusMessageList = this.statusMessageEauth(eAuthInfo, eAuthApprovers, (isSubmitAndCreate ?? false), submitAndCreateStatus);
      }
      this.status = this.statusMessage(status, eAuthInfo, eAuthApprovers, (isSubmitAndCreate ?? false), submitAndCreateStatus);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get hasMultipleToAccountsAccess(): boolean {
    return this.ffs.featureFlags.positionLotsPhase2Enabled || this.positionLotsPhase2EnabledFsg;
  }

  get isPositionsTransaction(): boolean {
    return this.extraPrompts?.transferType === 'positions' || this.extraPrompts?.transferType === 'positionsCash';
  }

  public showPositionsOrMultipleAccounts(): boolean {
    return this.hasMultipleToAccountsAccess && this.isPositionsTransaction;
  }

  statusMessage(
    status: CodeAndDescription,
    eAuthInfo: { isOptedOut: boolean, selectedAccountHolder: string[] } | undefined,
    customerList: EAuthStatusMessageFields[] | undefined,
    isSubmitAndCreate: boolean,
    submitAndCreateStatus: SubmitAndCreateStatus
  ): string {
    const isEAuth = !eAuthInfo?.isOptedOut;
    const selectedAccountHolder = customerList?.find(customer => customer.customerId === eAuthInfo?.selectedAccountHolder[0]);
    const email = selectedAccountHolder?.emailId || this.eAuthCustomers?.find((customer) => customer.customerId === selectedAccountHolder?.customerId)?.emailId;
    
    // if ach and schwab review
    if (!this.hasMmInitiateAccess && this.transactionType === transactionTypes.ach && this.pendReasons.includes(this.pendReasonStrings.schwabReview)) {
      return `Your transaction was received and is in process. You can view its status in the Account Transactions table in Move Money.`;
    }

    //if check, pend reason is ndfa/lpfa and use standingAuth is bundled return pending
    if (this.transactionType === transactionTypes.check && this.pendReasons.includes(this.pendReasonStrings.ndfaLPFA) && this.checkExtraPrompts?.useStandingAuth === 'bundled') {
      return 'Pending';
    }

    if (this.hasMmInitiateAccess && !this.canRelease
      && !this.isOneTimeTotalDist && !this.isChecksBundledOneTimeTotalDist) {
      if (isEAuth) {
        return `${this.namePipe.transform(status.description)} - Awaiting authorization from ${this.namePipe.transform(selectedAccountHolder?.firstName)} ${this.namePipe.transform(selectedAccountHolder?.lastName)} at ${email}`;
      } else if (this.eAuthModel.custAcctAuthorizationsList?.eAuthCustomerList?.length == 0) {
        return `${this.namePipe.transform(status.description)} - Request received. Schwab is reviewing.`;
      } else {
        return status.description === 'Awaiting signed authorization' ? status.description: `${this.namePipe.transform(status.description)} - Awaiting signed authorization`;
      }
    }

    // if pending auth and eauth and there are eauth approvers
    if (this.isPendingAuthorization && isEAuth && selectedAccountHolder) {
      return (isSubmitAndCreate && customerList) ? `${status.description} - ${this.getSubmitAndCreateEAuthStatus(selectedAccountHolder, customerList, submitAndCreateStatus)}` : `${status.description} - Awaiting authorization from ${this.namePipe.transform(selectedAccountHolder?.firstName)} ${this.namePipe.transform(selectedAccountHolder?.lastName)} at ${email}`;
      // pdf flows
    }
    else if (this.isPendingAuthorization &&
      (this.pendReasons.includes(this.pendReasonStrings.clientAuthLOA) ||
        this.pendReasons.includes(this.pendReasonStrings.ndfaLPFA) ||
        this.pendReasons.includes(this.pendReasonStrings.taxElections))
    ) {
      return status.description === 'Awaiting signed authorization' ? `${status.description}` : `${status.description} - Awaiting signed authorization`;
    }
    else if (this.pendReasons.length > 0) {
      return `${status.description} - Request received. Schwab is reviewing.`;
    }
    else if (this.isOneTimeTotalDist || this.isChecksBundledOneTimeTotalDist) {
      return confirmationStatusMessage.onetimeTotalDistribution;
    }
    else {
      return `${status.description}`
    }
  }

  private getSubmitAndCreateEAuthStatus(
    transactionAccountHolder: EAuthStatusMessageFields,
    customerList: EAuthStatusMessageFields[],
    submitAndCreateStatus: SubmitAndCreateStatus
  ): string {
    let namesAndEmails = customerList.map((customer) => {
      return `${this.namePipe.transform(
        customer?.firstName
      )} ${this.namePipe.transform(customer?.lastName)} at ${customer.emailId}`;
    });

    namesAndEmails = [`${this.namePipe.transform(
      transactionAccountHolder?.firstName
    )} ${this.namePipe.transform(transactionAccountHolder?.lastName)} at ${transactionAccountHolder?.emailId}`];

    const emailList = `${namesAndEmails.join(' and ')}.`;

    const transactionString = `Awaiting transaction authorization from ${this.namePipe.transform(
      transactionAccountHolder?.firstName
    )} ${this.namePipe.transform(transactionAccountHolder?.lastName)} at ${transactionAccountHolder?.emailId}`;

    if (submitAndCreateStatus.hasError) {
      if (submitAndCreateStatus.standingAuthFailed) {
        return `Standing authorization request failed. ${transactionString}`;
      }

      if (submitAndCreateStatus.transactionFailed) {
        return `Transaction failed. Awaiting standing authorization approval from ${emailList}`;
      }
    }

    const standingAuthString = `and standing authorization approval from`;

    return `${transactionString} ${standingAuthString} ${emailList}`;
  }

  getCaseId() {
    return this.itsInfo?.multipleAcknowledgeListById[this.toAccountInContext]?.caseId ?? '';
  }

  getSource() {
    if (this.itAckListResponse[this.toAccountInContext]?.hasError) {
      return 'N/A'
    }
    let sourceDetails = this.itAckListResponse[this.toAccountInContext]?.source.description;
    if (sourceDetails === sourceOfLastModification.Schwabinstitutional) {
      return sourceOfLastModification.SchwabAdvisorCenter ?? '';
    }
    return sourceOfLastModification.SchwabAdvisorCenter;
  }

  getUser(): string {
    if (this.canRelease) return this.username;
    if (this.initiatedBy) return this.initiatedBy;
    if (this.itAckListResponse[this.toAccountInContext]?.hasError) return 'N/A';
    return this.itAckListResponse[this.toAccountInContext]?.loginId ?? this.username;
  }

  getInitiatedBy(): string {
    return this.initiatedBy ? this.initiatedBy : this.username;
  }

  get lastModifiedBy(): string {
    return this.username;
  }

  getRequestedDate() {
    if (this.itAckListResponse[this.toAccountInContext]?.hasError) {
      return 'N/A'
    }
    if (this.isOneTimeTotalDist || this.is401kToAccount) {
      return (this.itsInfo?.processDate ?? '').replace(/\//g, '-');
    }
    return this.itAckListResponse[this.toAccountInContext]?.requestedDate ?? '';
  }

  getRequestedTime() {
    if (this.isOneTimeTotalDist || this.is401kToAccount) {
      return this.submittedEasternTime;
    }
    return this.itAckListResponse[this.toAccountInContext]?.requestedTime ?? '';
  }

  getCurrentEasternTimeString(): string {
    const now = new Date();
    let timeString = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/New_York' });
    return `${timeString} ET`;
  }

  // to fetch Eauth message for multiaccounts
  statusMessageEauth(
    eAuthInfo: { isOptedOut: boolean, selectedAccountHolder: string[] } | undefined,
    customerList: EAuthStatusMessageFields[] | undefined,
    isSubmitAndCreate: boolean,
    submitAndCreateStatus: SubmitAndCreateStatus
  ): string[] {
    const isEAuth = !eAuthInfo?.isOptedOut;
    const selectedAccountHolder = customerList?.find(customer => customer.customerId === eAuthInfo?.selectedAccountHolder[0]);
    const email = selectedAccountHolder?.emailId ||
      this.eAuthCustomers?.find((customer) => customer.customerId === selectedAccountHolder?.customerId)?.emailId;
    let statusDescription = this.titleCasePipe.transform(this.itAckListResponse[this.toAccountInContext]?.status?.description);

    if (this.isPendingAuthorization && isEAuth && selectedAccountHolder) {
      this.statusEauthmessage = (isSubmitAndCreate && customerList) ? `${statusDescription} - ${this.getSubmitAndCreateEAuthStatus(selectedAccountHolder, customerList, submitAndCreateStatus)}`
        : `${statusDescription} - Awaiting authorization from ${this.namePipe.transform(selectedAccountHolder?.firstName)} ${this.namePipe.transform(selectedAccountHolder?.lastName)} at ${email}`;
      return this.statusEauthmessage;
    }
    return this.statusEauthmessage;
  }

  get dateAndTimeStampLabel(): string {
    return  this.hasMmReleaseAccess && this.canRelease && this.draftInContext ? 'Released on: ' :
           this.hasMmInitiateAccess  && !this.canRelease && !this.isOneTimeTotalDist ? 'Initiated date: ' :
           'Submitted on: ';
  }

  get dateAndTimeStampLabelMultipleToAccount(): string {
    if (this.journalAuthResponse !== undefined){
      return this.hasMmReleaseAccess && this.journalAuthResponse?.canRelease && this.draftInContext ? 'Released on: ' :
           this.hasMmInitiateAccess  && !this.journalAuthResponse?.canRelease ? 'Initiated date: ' :
           'Submitted on: ';
    } else {
      return this.dateAndTimeStampLabel;
    }
  }

  get customRuleIandRFlowOff(): boolean {
      return (this.isNotInitReleaseFlowCustomRules && !this.canRelease && this.hasMmReleaseAccess);
  }

  getStatus() {
    let isInitiator = this.hasMmInitiateAccess && !this.journalAuthResponse?.canRelease;
    let pendReasons: string[] = [];
    let statusDescription = this.titleCasePipe.transform(this.itAckListResponse[this.toAccountInContext]?.status?.description);
    let authResponse = this.itAckListResponse[this.toAccountInContext]?.authorizeAttributeResponse;
    const isEAuth = !this.eAuthInfo?.isOptedOut;
    let isThirdParty: boolean = false;

    if (this.isOneTimeTotalDist || this.is401kToAccount) {
      return confirmationStatusMessage.onetimeTotalDistribution;
    }

    if (this.itAckListResponse[this.toAccountInContext]?.hasError) {
      return 'Failed'
    }

    if (isInitiator) {
      this.itjournalVerifyResponse.forEach(toAccount => {
        if (toAccount.toAccountInfo.accountNumber === this.toAccountInContext) {
          pendReasons = toAccount.pendReasons;
          isThirdParty = toAccount.isThirdParty;
        }
      })
    }
    else {
      pendReasons = this.itAckListResponse[this.toAccountInContext]?.pendReasons ?? [];
    }

    if (statusDescription !== "Approved" && (pendReasons?.length > 0 || authResponse && authResponse?.pendForRelease)) {
      if ((pendReasons.includes(this.pendReasonStrings.ndfaLPFA) ||
        pendReasons.includes(this.pendReasonStrings.clientAuthLOA) ||
        pendReasons.includes(this.pendReasonStrings.taxElections)) && !(isInitiator && isEAuth)) {

        // Get the email and account holder info and display if present
        const selectedAccountHolder = this.eAuthCustomers?.find(customer => customer.customerId === this.eAuthInfo?.selectedAccountHolder[0]);
        const email = selectedAccountHolder?.emailId;
        if (email && selectedAccountHolder) {
          return `${statusDescription} - Awaiting authorization from ${this.namePipe.transform(selectedAccountHolder.firstName)} ${this.namePipe.transform(selectedAccountHolder.lastName)} at ${email}`;
        }

        return `${statusDescription} - Awaiting signed authorization`;
      }
      if (pendReasons.includes(this.pendReasonStrings.clientAuthEauth) || (isInitiator && isEAuth && isThirdParty)) {
        return this.statusMessageList;
      }
      return `${statusDescription} - Request received. Schwab is reviewing.`;
    }
    return statusDescription ?? '';
  }
}
